/**
 * Parse assistant replies into intro text, numbered item cards, and outro.
 * Handles inline numbered lists with **Label:** value pairs (common MCP/agent output).
 */

const OUTRO_RE =
  /\s+(Let me know[^.!?]*[.!?]?|If you need[^.!?]*[.!?]?|Would you like[^.!?]*[.!?]?|Feel free[^.!?]*[.!?]?)$/i;

const SUMMARY_LABEL_RE =
  /^(paid|sent|unpaid|overdue|pending|draft|total|summary|outstanding|balance)$/i;

const SUMMARY_TAIL_RES = [
  /\s+(The\s+total\s+amount[\s\S]*?\.\s*)$/i,
  /\s+(total\s+amount[\s\S]*?\.\s*)$/i,
  /\s+(Total\s+(?:paid|received|outstanding|balance|invoiced|overdue|amount)[\s\S]*?\.\s*)$/i,
  /\s+(Overall[\s\S]*?\$[\d,]+(?:\.\d{2})?[\s\S]*?\.\s*)$/i,
  /\s+(In\s+(?:total|summary)[\s\S]*?\.\s*)$/i,
  /\s+(Amount\s+(?:paid|received|outstanding)[\s\S]*?\.\s*)$/i,
  /\s+(Outstanding\s+balance[\s\S]*?\.\s*)$/i,
  /\s+(You\s+have\s+[\s\S]*?\$[\d,]+(?:\.\d{2})?[\s\S]*?\.\s*)$/i,
];

const TITLE_LABEL_RE =
  /^(invoice no|invoice number|invoice #|customer|consultant|name|bill no|timesheet id|hr timesheet id|id|title)$/i;

const STATUS_LABEL_RE = /^(status|bill status|payment status|invoice status)$/i;

const FIELD_MARKER_RE = /\*\*([^*]+?):\*\*/g;

/** Max list index when detecting inline numbered cards (avoids $475.00 → "475." false positive). */
const MAX_INLINE_LIST_INDEX = 99;

/**
 * True when `index` points at "N. " that is part of a decimal/currency token, not a list marker.
 * @param {string} text
 * @param {number} index — start of digits before "."
 */
function isDecimalOrCurrencyFragment(text, index) {
  if (index <= 0) return false;
  const before = text.slice(Math.max(0, index - 12), index);
  if (/[$€£₹-]\s*$/.test(before)) return true;
  if (/\d$/.test(before)) return true;
  const after = text.slice(index);
  const tail = after.match(/^\d+\.(\s*)(\S)?/);
  if (!tail) return false;
  const [, space, nextChar] = tail;
  if (!space && nextChar && /\d/.test(nextChar)) return true;
  return false;
}

/**
 * Find start of a numbered card list, avoiding currency decimals like $475.00 or -$475.00.
 * @param {string} text
 * @returns {number}
 */
function findStructuredListStart(text) {
  const raw = String(text || '');
  if (!raw) return -1;

  const explicitRe = /\b(\d{1,2})\.\s+\*\*[^*]+:\*\*/g;
  let match = explicitRe.exec(raw);
  while (match) {
    const digitStart = match.index + match[0].indexOf(match[1]);
    if (!isDecimalOrCurrencyFragment(raw, digitStart)) {
      return match.index;
    }
    match = explicitRe.exec(raw);
  }

  const lineRe = /(?:^|\n)\s*(\d{1,2})\.\s+(?:\*\*|\S)/gm;
  while ((match = lineRe.exec(raw)) !== null) {
    const digitStart = match.index + match[0].search(/\d/);
    const num = parseInt(match[1], 10);
    if (num >= 1 && num <= MAX_INLINE_LIST_INDEX && !isDecimalOrCurrencyFragment(raw, digitStart)) {
      return match.index;
    }
  }

  const inlineRe = /\b(\d{1,2})\.\s+(?:\*\*[^*]+:\*\*|\S)/g;
  while ((match = inlineRe.exec(raw)) !== null) {
    const digitStart = match.index;
    const num = parseInt(match[1], 10);
    if (num < 1 || num > MAX_INLINE_LIST_INDEX) continue;
    if (isDecimalOrCurrencyFragment(raw, digitStart)) continue;
    if (/\*\*[^*]+:\*\*/.test(match[0])) return match.index;
  }

  return -1;
}

/** Remove markdown bold markers and trim — for plain table/cell display. */
export function stripMarkdownInline(text) {
  if (text == null) return '';
  return String(text).replace(/\*\*/g, '').trim();
}

/**
 * @param {string} text
 * @returns {{ bold: boolean, text: string }[]}
 */
export function splitInlineMarkdown(text) {
  if (typeof text !== 'string' || !text) return [];

  const parts = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let match;

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ bold: false, text: text.slice(last, match.index) });
    }
    parts.push({ bold: true, text: match[1] });
    last = match.index + match[0].length;
  }

  if (last < text.length) {
    parts.push({ bold: false, text: text.slice(last) });
  }

  if (parts.length === 0) {
    parts.push({ bold: false, text });
  }

  return parts;
}

/**
 * @param {string} label
 * @returns {'status' | 'default'}
 */
function fieldKind(label) {
  return STATUS_LABEL_RE.test(String(label || '').trim()) ? 'status' : 'default';
}

/**
 * @param {string} value
 * @returns {string}
 */
function cleanFieldValue(value) {
  return String(value || '')
    .replace(/\*\*/g, '')
    .replace(/^\s*-\s*/, '')
    .replace(/\s*-\s*$/, '')
    .trim();
}

/**
 * Parse body of a numbered item into optional title prefix + label/value fields.
 * Supports both:
 *   Name **Period:** May 1 - May 31 **Hours:** 40
 *   **Consultant:** Name - **Email:** x@y.com
 *
 * @param {string} segment
 * @returns {{ titlePrefix: string, fields: { label: string, value: string, kind: string }[] }}
 */
function parseItemFields(segment) {
  const cleaned = String(segment || '')
    .replace(/^\d+\.\s*/, '')
    .trim();

  if (!cleaned) {
    return { titlePrefix: '', fields: [] };
  }

  const markers = [];
  const re = new RegExp(FIELD_MARKER_RE.source, 'g');
  let match;

  while ((match = re.exec(cleaned)) !== null) {
    markers.push({
      label: match[1].trim(),
      markerStart: match.index,
      valueStart: match.index + match[0].length,
    });
  }

  if (markers.length === 0) {
    return {
      titlePrefix: '',
      fields: [
        {
          label: 'Details',
          value: cleaned.replace(/\*\*/g, '').trim(),
          kind: 'default',
        },
      ],
    };
  }

  const titlePrefix = cleaned.slice(0, markers[0].markerStart).trim();
  const fields = [];

  for (let i = 0; i < markers.length; i += 1) {
    const valueEnd = i + 1 < markers.length ? markers[i + 1].markerStart : cleaned.length;
    const rawValue = cleaned.slice(markers[i].valueStart, valueEnd);
    const label = markers[i].label;
    fields.push({
      label,
      value: cleanFieldValue(rawValue),
      kind: fieldKind(label),
    });
  }

  return { titlePrefix, fields };
}

/**
 * @param {string} body
 * @param {string} titlePrefix
 * @param {{ label: string, value: string, kind: string }[]} fields
 * @returns {{ title: string, fields: { label: string, value: string, kind: string }[] }}
 */
/**
 * @param {string} text
 * @returns {{ main: string, summary: string }}
 */
function extractSummaryTail(text) {
  const raw = String(text || '').trim();
  if (!raw) return { main: '', summary: '' };

  for (const re of SUMMARY_TAIL_RES) {
    const match = raw.match(re);
    if (match?.[1]) {
      return {
        main: raw.slice(0, match.index).trim(),
        summary: match[1].trim(),
      };
    }
  }

  return { main: raw, summary: '' };
}

/**
 * @param {string} summaryText
 * @param {string} [explicitLabel]
 * @returns {{ label: string, text: string, amount: string, parts: { bold: boolean, text: string }[] }}
 */
function parseSummaryMeta(summaryText, explicitLabel = '') {
  const raw = String(summaryText || '').trim();
  if (!raw) {
    return { label: '', text: '', amount: '', parts: [] };
  }

  let label = explicitLabel;
  let body = raw;

  if (!label) {
    const labelPrefix = raw.match(
      /^([A-Za-z]+(?:\s*\([^)]+\))?)\s+(?=The\s+total\b|Total\s+(?:paid|received|outstanding|balance|invoiced|overdue|amount)\b|Overall\b|Amount\b|Outstanding\b|In\s+)/i
    );
    if (labelPrefix && SUMMARY_LABEL_RE.test(labelPrefix[1])) {
      label = labelPrefix[1];
      body = raw.slice(labelPrefix[0].length).trim();
    }
  }

  if (/^total\s+amount/i.test(body)) {
    body = `The ${body}`;
  }

  const amountMatch = body.match(/\$[\d,]+(?:\.\d{2})?/);
  const amount = amountMatch ? amountMatch[0] : '';

  return {
    label,
    text: body,
    amount,
    parts: splitInlineMarkdown(body),
  };
}

function resolveItemTitle(body, titlePrefix, fields) {
  const titleField = fields.find((f) => TITLE_LABEL_RE.test(f.label));
  let title = titleField ? titleField.value : titlePrefix;
  let displayFields =
    titleField && fields.length > 1 ? fields.filter((f) => f !== titleField) : [...fields];

  if (!title && displayFields.length === 1 && displayFields[0].label === 'Details') {
    const details = displayFields[0].value;
    const shortTitle = details.length > 48 ? `${details.slice(0, 45).trim()}…` : details;
    return { title: shortTitle, fields: [] };
  }

  if (!title && displayFields.length > 0) {
    const first = displayFields[0];
    if (first.value.length <= 40 && !STATUS_LABEL_RE.test(first.label)) {
      title = first.value;
      displayFields = displayFields.slice(1);
    }
  }

  if (!title) {
    const plain = body.replace(/\*\*/g, '').trim();
    if (plain.length <= 52) {
      title = plain;
      displayFields = [];
    }
  }

  return { title: title || '', fields: displayFields };
}

function isMarkdownTableSeparatorLine(line) {
  const t = String(line || '').trim();
  if (!t.includes('|') && !t.includes('-')) return false;
  return /^[|\s:-]+$/.test(t) && /-/.test(t);
}

function parseMarkdownTableCells(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed.includes('|')) return [];
  let inner = trimmed;
  if (inner.startsWith('|')) inner = inner.slice(1);
  if (inner.endsWith('|')) inner = inner.slice(0, -1);
  return inner.split('|').map((c) => c.trim());
}

/**
 * @param {string[]} lines
 * @returns {{ headers: string[], rows: string[][] } | null}
 */
function parseMarkdownTableLines(lines) {
  const tableLines = (lines || []).map((l) => String(l || '').trim()).filter((l) => l.includes('|'));
  if (tableLines.length < 2) return null;

  const headers = parseMarkdownTableCells(tableLines[0]);
  if (!headers.length) return null;

  let dataStart = 1;
  if (tableLines.length > 1 && isMarkdownTableSeparatorLine(tableLines[1])) {
    dataStart = 2;
  }

  const rows = tableLines
    .slice(dataStart)
    .map(parseMarkdownTableCells)
    .filter((row) => row.length && row.some((cell) => cell));

  if (!rows.length) return null;
  return { headers, rows };
}

/**
 * Split reply text into alternating prose and markdown table blocks.
 * @param {string} text
 * @returns {{ type: 'text', content: string } | { type: 'table', headers: string[], rows: string[][] }[]}
 */
export function splitMarkdownTables(text) {
  const raw = String(text || '');
  if (!raw.trim()) return [];

  const lines = raw.split('\n');
  const segments = [];
  let textBuffer = [];
  let i = 0;

  const flushText = () => {
    const content = textBuffer.join('\n').trim();
    textBuffer = [];
    if (content) segments.push({ type: 'text', content });
  };

  while (i < lines.length) {
    const line = lines[i];
    const cells = parseMarkdownTableCells(line);
    const looksLikeTableRow = cells.length >= 2;

    if (looksLikeTableRow) {
      const block = [line];
      let j = i + 1;
      while (j < lines.length) {
        const next = lines[j];
        if (!String(next).trim()) break;
        const nextCells = parseMarkdownTableCells(next);
        if (nextCells.length < 2 && !isMarkdownTableSeparatorLine(next)) break;
        if (!next.includes('|') && !isMarkdownTableSeparatorLine(next)) break;
        block.push(next);
        j += 1;
      }

      const table = parseMarkdownTableLines(block);
      if (table) {
        flushText();
        segments.push({ type: 'table', ...table });
        i = j;
        continue;
      }
    }

    textBuffer.push(line);
    i += 1;
  }

  flushText();
  return segments;
}

function parseStructuredBlock(raw) {
  const firstItemIdx = findStructuredListStart(raw);
  const emptyBlock = {
    intro: '',
    introParts: [],
    items: [],
    summary: '',
    summaryLabel: '',
    summaryAmount: '',
    summaryParts: [],
    outro: '',
    outroParts: [],
    plainParts: [],
    hasStructuredItems: false,
  };

  if (firstItemIdx === -1) {
    return {
      ...emptyBlock,
      plainParts: splitInlineMarkdown(raw),
    };
  }

  const intro = raw.slice(0, firstItemIdx).trim().replace(/:\s*$/, '');
  const remainder = raw.slice(firstItemIdx).trim();
  const itemChunks = remainder.split(/\s+(?=\d{1,2}\.\s+(?:\*\*|\S))/);
  const items = [];
  let outro = '';
  let summary = '';
  let summaryLabel = '';

  itemChunks.forEach((chunk, idx) => {
    const numMatch = chunk.match(/^(\d+)\.\s+([\s\S]*)$/);
    if (!numMatch) return;

    let body = numMatch[2].trim();
    if (idx === itemChunks.length - 1) {
      const outroMatch = body.match(OUTRO_RE);
      if (outroMatch) {
        outro = outroMatch[1].trim();
        body = body.slice(0, outroMatch.index).trim();
      }

      const summaryExtract = extractSummaryTail(body);
      if (summaryExtract.summary) {
        summary = summaryExtract.summary;
        body = summaryExtract.main;
      }
    }

    const { titlePrefix, fields } = parseItemFields(body);
    const { title, fields: displayFields } = resolveItemTitle(body, titlePrefix, fields);

    items.push({
      index: parseInt(numMatch[1], 10),
      title,
      fields: displayFields,
    });
  });

  if (items.length === 0) {
    return {
      ...emptyBlock,
      plainParts: splitInlineMarkdown(raw),
    };
  }

  const hasExplicitFields = items.some((item) =>
    item.fields.some((f) => f.label && f.label !== 'Details')
  );
  const looksLikeSingleValueAnswer =
    items.length === 1 &&
    !hasExplicitFields &&
    intro.length > 0 &&
    /\$\s*$|-\$\s*$/.test(intro);

  if (looksLikeSingleValueAnswer) {
    return {
      ...emptyBlock,
      plainParts: splitInlineMarkdown(raw),
    };
  }

  if (!summary && items.length > 0) {
    const lastItem = items[items.length - 1];
    if (lastItem.fields.length > 0) {
      const lastField = lastItem.fields[lastItem.fields.length - 1];
      const extracted = extractSummaryTail(lastField.value);
      if (extracted.summary) {
        summary = extracted.summary;
        if (STATUS_LABEL_RE.test(lastField.label) && extracted.main) {
          summaryLabel = extracted.main;
        }
        lastField.value = extracted.main || lastField.value;
      }
    }
  }

  if (summary && !summaryLabel && items.length > 0) {
    const lastItem = items[items.length - 1];
    const statusField = lastItem.fields.find((field) => STATUS_LABEL_RE.test(field.label));
    if (statusField?.value && SUMMARY_LABEL_RE.test(statusField.value)) {
      summaryLabel = statusField.value;
    }
  }

  const summaryMeta = parseSummaryMeta(summary, summaryLabel);

  return {
    intro,
    introParts: splitInlineMarkdown(intro),
    items,
    summary: summaryMeta.text,
    summaryLabel: summaryMeta.label,
    summaryAmount: summaryMeta.amount,
    summaryParts: summaryMeta.parts,
    outro,
    outroParts: splitInlineMarkdown(outro),
    plainParts: [],
    hasStructuredItems: true,
  };
}

/**
 * @param {string} header
 * @returns {'debit' | 'credit' | 'default'}
 */
export function journalTableColumnKind(header) {
  const h = String(header || '').toLowerCase();
  if (/\bdebit\b/.test(h)) return 'debit';
  if (/\bcredit\b/.test(h)) return 'credit';
  return 'default';
}

/**
 * @param {string} text
 * @returns {{
 *   intro: string,
 *   introParts: { bold: boolean, text: string }[],
 *   items: { index: number, title: string, fields: { label: string, value: string, kind: string }[] }[],
 *   summary: string,
 *   summaryLabel: string,
 *   summaryAmount: string,
 *   summaryParts: { bold: boolean, text: string }[],
 *   outro: string,
 *   outroParts: { bold: boolean, text: string }[],
 *   plainParts: { bold: boolean, text: string }[],
 *   hasStructuredItems: boolean,
 *   hasSegments: boolean,
 *   segments: Array<
 *     | { type: 'table', headers: string[], rows: string[][] }
 *     | ({ type: 'text' } & ReturnType<typeof parseStructuredBlock>)
 *   >,
 * }}
 */
export function parseAssistantReply(text) {
  const raw = typeof text === 'string' ? text.trim() : '';
  const empty = {
    intro: '',
    introParts: [],
    items: [],
    summary: '',
    summaryLabel: '',
    summaryAmount: '',
    summaryParts: [],
    outro: '',
    outroParts: [],
    plainParts: [],
    hasStructuredItems: false,
    hasSegments: false,
    segments: [],
  };

  if (!raw) return empty;

  const tableSegments = splitMarkdownTables(raw);
  const hasTables = tableSegments.some((seg) => seg.type === 'table');

  if (hasTables) {
    return {
      ...empty,
      hasSegments: true,
      segments: tableSegments.map((seg) => {
        if (seg.type === 'table') return seg;
        return { type: 'text', ...parseStructuredBlock(seg.content) };
      }),
    };
  }

  const structured = parseStructuredBlock(raw);
  return {
    ...empty,
    ...structured,
  };
}
